import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Mail, ArrowRight, Phone, Lock, Shield, Users, Truck, Clock, AlertCircle, Eye, EyeOff, X, Check, RefreshCw, CheckCircle } from 'lucide-react';
import { authService } from '../services';
import rateLimiter from '../utils/rateLimiting';
import { validateLoginForm, formatPhoneNumber, cleanPhoneNumber } from '../utils/validationSchemas';
import { detectContactType } from '../utils/contactDetection';
import CountryCodeSelector from '../components/ui/CountryCodeSelector';
import { useAuth } from '../contexts/AuthContext';
import ForgotPasswordModal from '../components/modals/ForgotPasswordModal';
import { getDashboardRoute } from '../utils/roleNavigation';
import GroupSvg from '../svg/Group.svg';
import { isTokenExpired, handleTokenExpiry } from '../utils/authUtils';
import { API_CONFIG } from '../config/api';
import { getLocalStorage } from '../utils/browserCheck';

const LoginPage = ({ dashboardType = 'admin' }) => {
  console.log('🎨 [LoginPage] Component rendering/re-rendering');

  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, isLoading: authLoading, initializeAuth, setUserDirectly } = useAuth();
  const [loginOption, setLoginOption] = useState('email');
  const [formData, setFormData] = useState({
    email: location.state?.contact || '',
    phone: '',
    phone_country_code: '+91', // Default to India
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState(location.state?.message || '');
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [errorTimeout, setErrorTimeout] = useState(null);
  const [errorCountdown, setErrorCountdown] = useState(0);

  // Sync message or contact if location state updates
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
    }
    if (location.state?.contact) {
      setFormData(prev => ({
        ...prev,
        email: location.state.contact
      }));
    }
  }, [location.state]);

  // Forgot Password States
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState('email'); // 'email' | 'otp' | 'newPassword'
  const [forgotPasswordData, setForgotPasswordData] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordErrors, setForgotPasswordErrors] = useState({});
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);

  // Redirect authenticated users to dashboard — single domain now, so this
  // is purely role-based (getDashboardRoute sends an unmapped role to
  // /no-dashboard-access rather than looping back here).
  useEffect(() => {
    if (!authLoading && isAuthenticated && user && !isLoading && !errors.general) {
      const dashboardRoute = getDashboardRoute(user.role, user);
      navigate(dashboardRoute, { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate, isLoading, errors.general]);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (errorTimeout) {
        clearTimeout(errorTimeout);
      }
    };
  }, [errorTimeout]);


  // Function to clear errors with auto-hide
  const clearErrorsWithTimeout = useCallback(() => {
    console.log('⏰ [LoginPage] Setting up error auto-hide');

    // Clear existing timeout
    if (errorTimeout) {
      console.log('⏰ [LoginPage] Clearing existing timeout');
      clearTimeout(errorTimeout);
    }

    // Start countdown
    console.log('⏰ [LoginPage] Starting 7 second countdown');
    setErrorCountdown(7);

    // Set new timeout to clear errors after 7 seconds
    const timeout = setTimeout(() => {
      console.log('⏰ [LoginPage] Auto-hiding error after 7 seconds');
      setErrors({});
      setRateLimitInfo(null);
      setErrorCountdown(0);
    }, 7000);

    setErrorTimeout(timeout);

    // Update countdown every second
    let countdown = 7;
    const countdownInterval = setInterval(() => {
      countdown--;
      console.log(`⏰ [LoginPage] Countdown: ${countdown}s remaining`);
      setErrorCountdown(countdown);

      if (countdown <= 0) {
        clearInterval(countdownInterval);
      }
    }, 1000);

    // Store interval ID for cleanup
    return () => {
      clearInterval(countdownInterval);
      clearTimeout(timeout);
    };
  }, [errorTimeout]);

  // Memoized input change handler
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;

    // Clean phone number input
    const processedValue = name === 'phone' ? cleanPhoneNumber(value) : value;

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear rate limit info
    if (rateLimitInfo) {
      setRateLimitInfo(null);
    }

    // Clear any existing error timeout
    if (errorTimeout) {
      clearTimeout(errorTimeout);
      setErrorTimeout(null);
      setErrorCountdown(0);
    }
  }, [errors, rateLimitInfo, errorTimeout]);

  // Memoized form validation using Yup or fallback
  const validateForm = useCallback(async () => {
    // If loginOption is 'email', detect if the input is actually a phone number
    let actualLoginType = loginOption;
    let validationData = {
      ...formData,
      [loginOption]: formData[loginOption]
    };

    if (loginOption === 'email' && formData.email) {
      const trimmedEmail = formData.email.trim();
      const detection = detectContactType(trimmedEmail);

      console.log('🔍 [LoginPage] Contact detection result:', {
        input: trimmedEmail,
        type: detection.type,
        phone: detection.phone,
        countryCode: detection.countryCode
      });

      if (detection.type === 'phone') {
        // It's actually a phone number, validate as phone
        actualLoginType = 'phone';
        // Clean the phone number to ensure it's only digits
        const cleanedPhone = detection.phone ? cleanPhoneNumber(detection.phone) : cleanPhoneNumber(trimmedEmail);
        const countryCode = detection.countryCode || formData.phone_country_code || '+91';

        console.log('📱 [LoginPage] Validating as phone:', { cleanedPhone, countryCode });

        validationData = {
          ...formData,
          phone: cleanedPhone,
          phone_country_code: countryCode
        };
      } else if (detection.type === 'email') {
        // It's an email, validate as email
        actualLoginType = 'email';
        validationData = {
          ...formData,
          email: detection.email || trimmedEmail
        };
      } else if (detection.type === 'unknown') {
        // Invalid input - show error on email field
        setErrors({ email: 'Please enter a valid email address or phone number' });
        return false;
      }
    }

    const { errors: validationErrors, isValid } = await validateLoginForm(actualLoginType, validationData);

    // Map phone validation errors to email field if user entered phone in email field
    if (loginOption === 'email' && actualLoginType === 'phone') {
      if (validationErrors.phone) {
        validationErrors.email = validationErrors.phone;
        delete validationErrors.phone;
      }
      if (validationErrors.phone_country_code) {
        // Map country code error to email field as well
        validationErrors.email = validationErrors.phone_country_code;
        delete validationErrors.phone_country_code;
      }
    }

    setErrors(validationErrors);
    return isValid;
  }, [formData, loginOption]);

  // Helper function to get contact based on login option
  const getContact = useCallback(() => {
    if (loginOption === 'email') {
      return formData.email;
    }
    return formData.phone;
  }, [loginOption, formData]);

  // Helper function to check and handle rate limiting
  const checkRateLimit = useCallback((contact) => {
    const rateLimitCheck = rateLimiter.checkRateLimit(contact);
    if (!rateLimitCheck.allowed) {
      setRateLimitInfo(rateLimitCheck);
      setErrors({
        general: rateLimitCheck.message || 'Too many attempts. Please try again later.'
      });
      return false;
    }
    return true;
  }, []);

  // Helper function to check if error is authentication error
  const isAuthenticationError = useCallback((errorMessage) => {
    if (!errorMessage) return false;
    return (
      errorMessage.includes('Invalid') ||
      errorMessage.includes('User not found') ||
      errorMessage.includes('password') ||
      errorMessage.includes('email')
    );
  }, []);

  // Helper function to process and set error
  const processError = useCallback((error) => {
    const errorMessage = error.message || error.toString();
    console.log('🔍 [LoginPage] Processing error message:', errorMessage);

    let generalMessage = errorMessage || 'Failed to send OTP. Please try again.';

    if (isAuthenticationError(errorMessage)) {
      console.log('🔍 [LoginPage] Setting authentication error');
      generalMessage = errorMessage || 'Invalid email/phone or password. Please check your credentials and try again.';
    } else {
      console.log('🔍 [LoginPage] Setting general error');
    }

    setErrors({ general: generalMessage });

    setTimeout(() => {
      console.log('🔍 [LoginPage] Errors state after setting:', errors);
      console.log('🔍 [LoginPage] Calling clearErrorsWithTimeout');
      clearErrorsWithTimeout();
    }, 100);
  }, [isAuthenticationError, clearErrorsWithTimeout, errors]);

  // Helper function to navigate to OTP page
  const navigateToOtpPage = useCallback((contact, userId, otp, actualLoginType) => {
    navigate('/otp', {
      state: {
        contact,
        password: formData.password,
        loginOption: actualLoginType || loginOption, // Use detected login type
        loginType: actualLoginType || loginOption, // Also pass as loginType for consistency
        userId,
        otp
      }
    });
  }, [navigate, formData.password, loginOption]);

  // Memoized form submission with rate limiting
  const handleSubmit = useCallback(async (e) => {
    // CRITICAL: Prevent default form submission FIRST
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    console.log('🔵 [LoginPage] Form submitted - preventDefault called');
    console.log('🔵 [LoginPage] Event:', e);
    console.log('🔵 [LoginPage] Current auth state:', { isAuthenticated, user: user?.email, authLoading });

    // Validate form first - early return
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    // Calculate formatted contact for rate limiting (same logic as in try block)
    let contactForRateLimit = getContact();
    if (loginOption === 'email' && formData.email) {
      const detection = detectContactType(formData.email.trim());
      if (detection.type === 'phone') {
        // For rate limiting, use the phone number with country code for consistency
        contactForRateLimit = `${detection.countryCode || formData.phone_country_code || '+91'}${detection.phone}`;
      } else {
        contactForRateLimit = formData.email.trim();
      }
    } else if (loginOption === 'phone') {
      // For rate limiting, use the phone number with country code for consistency
      contactForRateLimit = `${formData.phone_country_code}${formData.phone}`;
    }

    // Check rate limiting - early return
    if (!checkRateLimit(contactForRateLimit)) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    setRateLimitInfo(null);

    try {
      // Detect if email field contains a phone number
      let actualLoginType = loginOption;
      let formattedContact;
      let detection = null; // Store detection result for later use

      if (loginOption === 'email' && formData.email) {
        detection = detectContactType(formData.email.trim());
        if (detection.type === 'phone') {
          // It's actually a phone number - send just the digits without country code
          actualLoginType = 'phone';
          // Backend expects just the phone number digits, not with country code
          formattedContact = detection.phone ? cleanPhoneNumber(detection.phone) : cleanPhoneNumber(formData.email);
        } else {
          // It's an email
          formattedContact = formData.email.trim();
        }
      } else {
        // Phone option selected - send just the phone number digits
        formattedContact = cleanPhoneNumber(formData.phone);
        // For phone option, we can create a detection object
        if (formData.phone) {
          detection = {
            type: 'phone',
            phone: cleanPhoneNumber(formData.phone),
            countryCode: formData.phone_country_code || '+91'
          };
        }
      }

      // Single unified login flow for every role. sendLoginOtp validates
      // the password and returns the account's role WITHOUT issuing any
      // session token yet — that matters for Super Admin accounts, whose
      // OTP step needs to be a REAL second factor (no token should exist
      // until OTP is verified), not just a UI detour after a token was
      // already handed out. Every other role skips the OTP page entirely
      // and gets a normal token via authService.login() right after,
      // exactly like Hospital/Fleet/Command Center behaved before this was
      // one page — the only thing that changed is WHICH check decides that
      // (role, not which port/subdomain you happened to be on).
      const otpCheck = await authService.sendLoginOtp({
        contact: formattedContact,
        password: formData.password,
        loginType: actualLoginType
      });

      if (!otpCheck.success) {
        setIsLoading(false);
        return;
      }

      rateLimiter.recordSuccess(formattedContact);

      const userRole = otpCheck.data.userRole;
      // OTP is only required for Super Admin accounts. Every other role
      // (Hospital, Fleet, Command Center) logs in directly after password
      // verification — matching the backend's requiresOtp guard exactly.
      const requiresOtp = otpCheck.data?.requiresOtp !== undefined
        ? otpCheck.data.requiresOtp
        : (
            userRole === 'SUPER_ADMIN' ||
            userRole === 'SUPER_ADMIN_USER'
          );

      if (requiresOtp) {
        navigateToOtpPage(formattedContact, otpCheck.data.userId, otpCheck.data.otp, actualLoginType);
        return;
      }

      const result = await authService.login({
        contact: formattedContact,
        password: formData.password,
        loginType: actualLoginType
      });

      if (!result.success) {
        console.error('❌ [LoginPage] Login failed - result.success is false');
        setIsLoading(false);
        return;
      }

      // First-time login (registration created the account, but its
      // temporary password has never been changed): the backend returns a
      // 200 with requiresPasswordReset:true and NO token — send them to
      // reset their password instead of treating this as a normal
      // successful login. Without this check, setUserDirectly below would
      // mark the user "authenticated" with no real token ever stored, so
      // the very next API call 401s and bounces them straight back to
      // /login — looking like the page just "refreshes and does nothing."
      if (result.data?.requiresPasswordReset) {
        // FirstTimePasswordReset reads exactly these three state keys
        // (userData, contact, loginType) — see src/pages/FirstTimePasswordReset.jsx.
        navigate('/first-time-password-reset', {
          state: {
            userData: result.data.user,
            contact: formattedContact,
            loginType: actualLoginType
          }
        });
        return;
      }

      if (result.data?.user) {
        // Elevate city/state from whichever role-specific object the
        // backend attached (hospital for HOSPITAL_USER, fleet/operator for
        // TENANT_*/FLEET_ADMIN) — matches what hospitalLogin/fleetLogin
        // used to do themselves, for components that read user.city/state
        // directly (e.g. FleetPricingOverrides).
        const roleData = result.data.hospital || result.data.fleet || result.data.user.operator || result.data.operator;
        const userData = {
          ...result.data.user,
          ...(result.data.hospital && { hospital: result.data.hospital }),
          ...(result.data.fleet && { fleet: result.data.fleet }),
          city: result.data.user.city || roleData?.city || '',
          state: result.data.user.state || roleData?.state || ''
        };
        setUserDirectly(userData);
      }

      const roleToUse = result.data?.user?.role || userRole;
      const dashboardRoute = getDashboardRoute(roleToUse, result.data?.user);
      navigate(dashboardRoute);
    } catch (error) {
      console.error('❌ [LoginPage] Login error:', error);
      console.error('❌ [LoginPage] Error message:', error.message);

      // Record failed attempt for rate limiting - use formatted contact if available
      let contactForRateLimit = getContact();
      if (loginOption === 'email' && formData.email) {
        const detection = detectContactType(formData.email.trim());
        if (detection.type === 'phone') {
          // For rate limiting, use the phone number with country code for consistency
          contactForRateLimit = `${detection.countryCode || formData.phone_country_code || '+91'}${detection.phone}`;
        } else {
          contactForRateLimit = formData.email.trim();
        }
      } else if (loginOption === 'phone') {
        // For rate limiting, use the phone number with country code for consistency
        contactForRateLimit = `${formData.phone_country_code}${formData.phone}`;
      }
      rateLimiter.recordFailedAttempt(contactForRateLimit);

      // Process and set error
      processError(error);
    } finally {
      setIsLoading(false);
    }
  }, [formData, loginOption, validateForm, getContact, checkRateLimit, navigateToOtpPage, processError, isAuthenticated, user, authLoading, navigate, initializeAuth, setUserDirectly]);

  // Memoized login option change handler
  const handleLoginOptionChange = useCallback((option) => {
    setLoginOption(option);
    setFormData({ email: '', phone: '', password: '' });
    setErrors({});
    setRateLimitInfo(null);

    // Clear any existing error timeout
    if (errorTimeout) {
      clearTimeout(errorTimeout);
      setErrorTimeout(null);
      setErrorCountdown(0);
    }
  }, [errorTimeout]);

  // Memoized password visibility toggle handler
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const features = [
    {
      icon: Shield,
      title: 'Secure Platform',
      description: 'Enterprise-grade security for all your ambulance operations'
    },
    {
      icon: Users,
      title: 'Multi-User Support',
      description: 'Manage hospitals, fleet operators, and admin users seamlessly'
    },
    {
      icon: Truck,
      title: 'Real-time Tracking',
      description: 'Track ambulances and manage requests in real-time'
    }
  ];

  // Single domain now — one login page for every role, so branding is
  // generic rather than picked by dashboardType. Whether this account needs
  // an OTP step isn't knowable until after the password is validated (see
  // handleSubmit), so this can't say "Login" vs "Get OTP" up front either.
  const branding = {
    title: 'Login',
    description: 'Access your dashboard',
    icon: Lock,
    color: 'text-gray-600'
  };

  // Helper function to get dashboard type indicator className
  const getDashboardTypeClassName = () => 'bg-gray-50 border border-gray-200';

  // Helper function to get button content based on state
  const getButtonContent = (isLoading, rateLimitInfo) => {
    if (isLoading) {
      return (
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          <span>Signing in...</span>
        </div>
      );
    }

    if (rateLimitInfo && !rateLimitInfo.allowed) {
      return (
        <div className="flex items-center space-x-2">
          <Clock className="w-4 h-4" />
          <span>Rate Limited</span>
        </div>
      );
    }

    return (
      <div className="flex items-center space-x-2">
        <span>Sign In</span>
        <ArrowRight className="w-4 h-4" />
      </div>
    );
  };

  // Check for expired token before showing loading screen
  // If token is expired, redirect immediately without showing loading screen
  if (authLoading) {
    const storage = getLocalStorage();
    if (storage) {
      const token = storage.getItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN);
      if (token && isTokenExpired(token)) {
        console.warn('🚨 [LoginPage] Token is expired, redirecting to login immediately');
        // Clear auth and redirect without showing loading screen
        handleTokenExpiry('Token expired on login page');
        // Return null while redirect is happening
        return null;
      }
    }

    // Show loading spinner while checking authentication (only if token is not expired)
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#1e3a8a] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="w-full mx-auto">
        <div className="items-center">

          {/* Left Side - Features & Branding */}
          {/* <div className="hidden lg:block space-y-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-[#1e3a8a] rounded-xl flex items-center justify-center">
                  <Truck className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Smart Ambulance</h1>
              </div>
              <p className="text-lg font-semibold text-gray-600 max-w-md">
                Streamline your ambulance operations with our comprehensive management platform.
              </p>
            </div>

            <div className="space-y-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-[#1e3a8a] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div> */}

          {/* Right Side - Login Form */}
          <div className="w-full max-w-md mx-auto">
            <Card className="shadow-xl rounded-2xl bg-white/80 backdrop-blur-sm">
              <CardHeader className="space-y-2 text-center pt-6">
                <div className="flex justify-center mb-4">
                  <img src={GroupSvg} alt="Smart Ambulance Logo" className="h-10 w-auto" />
                </div>
                {/* Dashboard Type Indicator */}
                <div className="flex justify-center">
                  <div className={`inline-flex items-center gap-2 px-2 py-2 rounded-full ${getDashboardTypeClassName(dashboardType)}`}>
                    {React.createElement(branding.icon, {
                      className: `w-4 h-4 ${branding.color}`
                    })}
                    <span className={`text-sm font-semibold ${branding.color}`}>
                      {branding.title}
                    </span>
                  </div>
                </div>
                <CardDescription className="text-gray-600 mt-2">
                  {branding.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4 !pt-0">
                {/* Success Message Display */}
                {successMessage && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mr-2 flex-shrink-0" />
                      <p className="text-sm text-emerald-800 font-medium">{successMessage}</p>
                    </div>
                  </div>
                )}

                {/* Error Display */}
                {errors.general && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                      <p className="text-sm text-red-600">{errors.general}</p>
                    </div>
                  </div>
                )}

                {/* Rate Limiting Display */}
                {rateLimitInfo && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-yellow-600 mr-2" />
                      <div>
                        <p className="text-sm text-yellow-800 font-medium">Rate Limited</p>
                        <p className="text-xs text-yellow-700 mt-1">
                          {rateLimitInfo.message}
                        </p>
                        {rateLimitInfo.lockoutTime > 0 && (
                          <p className="text-xs text-yellow-700 mt-1">
                            Try again in {Math.ceil(rateLimitInfo.lockoutTime / 60000)} minutes
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Login Option Selection */}
                  {/* <div className="space-y-4">
                    <label className="block text-sm font-semibold text-gray-900">Choose Login Method</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => handleLoginOptionChange('email')}
                        className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-lg border-2 transition-all duration-200 ${loginOption === 'email'
                            ? 'border-[#1e3a8a] bg-[#1e3a8a]/5 text-[#1e3a8a]'
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                          }`}
                        disabled={isLoading}
                      >
                        <Mail className="w-4 h-4" />
                        <span className="text-sm font-medium">Email</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleLoginOptionChange('phone')}
                        className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-lg border-2 transition-all duration-200 ${loginOption === 'phone'
                            ? 'border-[#1e3a8a] bg-[#1e3a8a]/5 text-[#1e3a8a]'
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                          }`}
                        disabled={isLoading}
                      >
                        <Phone className="w-4 h-4" />
                        <span className="text-sm font-medium">Phone</span>
                      </button>
                    </div>
                  </div> */}

                  {/* Email Field */}
                  {loginOption === 'email' && (
                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-900">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          id="email"
                          name="email"
                          type="text"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`pl-12 sm:pl-14 ${errors.email ? 'border-red-300 focus:ring-red-500' : ''}`}
                          disabled={isLoading}
                          placeholder="Enter your email or phone number"
                        />
                      </div>
                      {errors.email && (
                        <p className="text-sm text-red-600">{errors.email}</p>
                      )}
                    </div>
                  )}

                  {/* Phone Field */}
                  {loginOption === 'phone' && (
                    <div className="space-y-2">
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-900">
                        Phone Number
                      </label>
                      <div className="flex gap-3">
                        <div className="w-24">
                          <CountryCodeSelector
                            value={formData.phone_country_code}
                            onChange={(e) => handleInputChange({ target: { name: 'phone_country_code', value: e.target.value } })}
                            disabled={isLoading}
                            placeholder="Country"
                          />
                        </div>
                        <div className="flex-1">
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className={`${errors.phone ? 'border-red-300 focus:ring-red-500' : ''}`}
                            disabled={isLoading}
                            placeholder="Enter your email or phone number"
                            maxLength={10}
                          />
                        </div>
                      </div>
                      {errors.phone && (
                        <p className="text-sm text-red-600">{errors.phone}</p>
                      )}
                    </div>
                  )}

                  {/* Password Field - Always required */}
                  <div className="space-y-2">
                    <label htmlFor="password" className="block text-sm font-semibold text-gray-900">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className={`pl-12 sm:pl-14 pr-12 sm:pr-14 ${errors.password ? 'border-red-300 focus:ring-red-500' : ''}`}
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:text-gray-600"
                        disabled={isLoading}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-sm text-red-600">{errors.password}</p>
                    )}
                  </div>

                  {/* Login Button */}
                  <Button
                    type="submit"
                    className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white font-medium py-3 rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:transform-none disabled:opacity-50"
                    disabled={isLoading || (rateLimitInfo && !rateLimitInfo.allowed)}
                  >
                    {getButtonContent(isLoading, rateLimitInfo)}
                  </Button>
                </form>

                {/* Divider */}
                {/* <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">Or continue with</span>
                  </div>
                </div> */}

                {/* Social Login Buttons */}
                {/* <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    className="w-full py-3 border-gray-200 hover:bg-gray-50 transition-colors"
                    disabled={isLoading}
                  >
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Google
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full py-3 border-gray-200 hover:bg-gray-50 transition-colors"
                    disabled={isLoading}
                  >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                    </svg>
                    Twitter
                  </Button>
                </div> */}

                {/* Sign Up Links */}
                <div className="text-center pt-4 space-y-3">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="w-full group relative overflow-hidden rounded-lg border-2 border-gray-200 hover:border-[#1e3a8a] bg-gradient-to-r from-gray-50 to-white hover:from-blue-50 hover:to-purple-50 p-3 transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    disabled={isLoading}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <RefreshCw className="w-4 h-4 text-gray-600 group-hover:text-[#1e3a8a] transition-colors" />
                      <span className="text-sm font-semibold text-gray-700 group-hover:text-[#1e3a8a] transition-colors">
                        Reset Your Password
                      </span>
                    </div>
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        step={forgotPasswordStep}
        setStep={setForgotPasswordStep}
        formData={forgotPasswordData}
        setFormData={setForgotPasswordData}
        loading={forgotPasswordLoading}
        setLoading={setForgotPasswordLoading}
        errors={forgotPasswordErrors}
        setErrors={setForgotPasswordErrors}
        success={forgotPasswordSuccess}
        setSuccess={setForgotPasswordSuccess}
        dashboardType={dashboardType}
      />
    </div>
  );
};

export default LoginPage;
